import { useEffect, useState, useCallback } from "react";
import { X } from "lucide-react";

/**
 * Visite guidée légère et sans dépendance externe : entoure un élément ciblé
 * (par sélecteur CSS) d'un halo et affiche une bulle d'explication à côté.
 * Ne s'affiche qu'une fois par visiteur (mémorisé dans localStorage).
 */
export default function ProductTour({ storageKey, steps }) {
  const [stepIndex, setStepIndex] = useState(-1);
  const [rect, setRect] = useState(null);

  const finish = useCallback(() => {
    try { localStorage.setItem(storageKey, "1"); } catch { /* ignore */ }
    setStepIndex(-1);
  }, [storageKey]);

  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem(storageKey) === "1"; } catch { /* ignore */ }
    if (!seen && steps.length > 0) {
      const timer = setTimeout(() => setStepIndex(0), 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [storageKey, steps.length]);

  useEffect(() => {
    if (stepIndex < 0) return undefined;
    const step = steps[stepIndex];
    if (!step) return undefined;

    const updateRect = () => {
      const el = document.querySelector(step.target);
      if (el) {
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null);
      }
    };

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [stepIndex, steps]);

  if (stepIndex < 0 || stepIndex >= steps.length) return null;
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  const tooltipStyle = rect
    ? {
        top: Math.min(rect.bottom + 12, window.innerHeight - 180),
        left: Math.min(Math.max(rect.left, 16), window.innerWidth - 336),
      }
    : { top: window.innerHeight / 2 - 80, left: window.innerWidth / 2 - 160 };

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {rect && (
        <div
          className="absolute rounded-xl ring-2 ring-blue-400 shadow-[0_0_0_4000px_rgba(0,0,0,0.55)] transition-all duration-300"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}
      <div
        className="absolute w-80 pointer-events-auto rounded-2xl border border-white/10 bg-[#12121a] p-5 shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        style={tooltipStyle}
      >
        <button
          onClick={finish}
          className="absolute top-3 right-3 text-white/30 hover:text-white/60"
          aria-label="Fermer la visite guidée"
        >
          <X className="w-4 h-4" />
        </button>
        <p className="text-xs font-mono text-blue-400 mb-1">{stepIndex + 1}/{steps.length}</p>
        <h3 className="text-white font-semibold mb-2">{step.title}</h3>
        <p className="text-white/60 text-sm leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <button onClick={finish} className="text-xs text-white/40 hover:text-white/60">Passer</button>
          <button
            onClick={() => (isLast ? finish() : setStepIndex((i) => i + 1))}
            className="rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors"
          >
            {isLast ? "Terminer" : "Suivant"}
          </button>
        </div>
      </div>
    </div>
  );
}
